import { Link } from 'react-router-dom';
import { Pencil } from 'lucide-react';
import type { DepartmentRow, DepartmentWithAgents } from '@command-room/shared';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/cn';

const STATUS_STYLES: Record<string, string> = {
  active: 'bg-success/15 text-success border-success/30',
  busy: 'bg-warning/20 text-warning-foreground border-warning/40',
  scheduled: 'bg-primary/15 text-primary border-primary/30',
  onboarding: 'bg-secondary text-secondary-foreground border-secondary',
  inactive: 'bg-muted text-muted-foreground border-muted',
};

function initials(name: string) {
  return name
    .split(/\s+/)
    .map((s) => s[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();
}

interface DepartmentSectionProps {
  dept: DepartmentWithAgents;
  onEdit?: (dept: DepartmentRow) => void;
}

export function DepartmentSection({ dept, onEdit }: DepartmentSectionProps) {
  const head = dept.head_agent_name
    ? dept.agents.find((a) => a.name === dept.head_agent_name) ?? null
    : null;

  const isVirtual = dept.id < 0;

  return (
    <section className="rounded-lg border bg-card">
      <div className="border-b px-5 py-4">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <h2 className="text-base font-semibold">{dept.name}</h2>
            {dept.goal && (
              <p className="mt-0.5 line-clamp-2 text-sm text-muted-foreground">{dept.goal}</p>
            )}
          </div>
          <div className="flex shrink-0 items-center gap-2">
            {head && (
              <Link
                to={`/agents/${encodeURIComponent(head.name)}`}
                className="flex items-center gap-1.5 rounded-full border bg-background px-2.5 py-1 text-xs font-medium hover:border-primary/40 hover:text-primary transition-colors"
              >
                <Avatar className="h-4 w-4">
                  {head.agent_icon_url && <AvatarImage src={head.agent_icon_url} alt={head.name} />}
                  <AvatarFallback className="text-[8px]">{initials(head.name)}</AvatarFallback>
                </Avatar>
                {head.name}
              </Link>
            )}
            <span className="rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground">
              {dept.agents.length} {dept.agents.length === 1 ? 'member' : 'members'}
            </span>
            {!isVirtual && onEdit && (
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7 text-muted-foreground hover:text-foreground"
                onClick={() => onEdit(dept)}
                aria-label={`Edit ${dept.name}`}
              >
                <Pencil className="h-3.5 w-3.5" />
              </Button>
            )}
          </div>
        </div>
        {dept.responsibilities && (
          <p className="mt-2 line-clamp-2 text-xs text-muted-foreground/70">
            {dept.responsibilities}
          </p>
        )}
      </div>

      {dept.agents.length === 0 ? (
        <p className="px-5 py-4 text-sm text-muted-foreground">No members.</p>
      ) : (
        <ul className="divide-y">
          {dept.agents.map((agent) => (
            <li key={agent.name}>
              <Link
                to={`/agents/${encodeURIComponent(agent.name)}`}
                className="flex items-center gap-3 px-5 py-3 hover:bg-muted/40 transition-colors"
              >
                <Avatar className="h-8 w-8 ring-1 ring-border">
                  {agent.agent_icon_url && (
                    <AvatarImage src={agent.agent_icon_url} alt={agent.name} />
                  )}
                  <AvatarFallback className="text-xs">{initials(agent.name)}</AvatarFallback>
                </Avatar>
                <div className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-medium">{agent.name}</span>
                  {agent.role && (
                    <span className="block truncate text-xs text-muted-foreground">{agent.role}</span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <Badge
                    variant={agent.type === 'AI' ? 'secondary' : 'outline'}
                    className="px-1.5 py-0 text-[10px]"
                  >
                    {agent.type ?? '—'}
                  </Badge>
                  <span
                    className={cn(
                      'rounded-full border px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider',
                      STATUS_STYLES[agent.status_badge] ?? STATUS_STYLES['inactive'],
                    )}
                  >
                    {agent.status_badge}
                  </span>
                </div>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
